<script setup>
const fullWidthMode = inject("fullWidthMode", false);
const halfCol = computed(() => (fullWidthMode ? undefined : 6));
import { useAnalysisStore } from '@/store/modules/analysis';
import {
  splitBySentenceWithAst,
  splitKeywordParts,
  splitBySentence,
  splitSentencesByDivider,
  formatDoubleHash,
  transformMarkedText,
} from '@/composables/splitSentence';
import PersoanlIljuChart from './PersoanlIljuChart.vue';
import PersonalFiveChart from './PersonalFiveChart.vue';
import SpeechBubbleTail from '@/components/svg/SpeechBubbleTail.vue';

const props = defineProps({
  pData: Object,
  userName: String,
});
const store = useAnalysisStore();
// const userName = computed(() => store.userName)
onMounted(() => {
  // pData.value = store.analysisResult;
});

/** 차트 데이터 */
const chartData = computed(() => props.pData.오행분석);

// 일주 차트 데이터
const defaultFive = computed(() => props.pData.오행분석.기본비율);
const jijangFive = computed(() => props.pData.오행분석.지장비율);

const seasonCategory = computed(() => props.pData.계절오행성향.카테고리);
const seasonAttribute = computed(() => props.pData.계절오행성향.기본특징);
const seasonAttributeHtml = computed(() => {
  return formatDoubleHash(seasonAttribute.value);
});
const seasonDetail = computed(() => {
  const sentences = splitBySentenceWithAst(props.pData.계절오행성향.상세특징 || '');
  return sentences.map((sentence) => transformMarkedText(sentence));
});
const fiveAnalysis = computed(() => props.pData.오행분석.오행분석);
const parsedFiveAnalysis = computed(() => parseFiveAnalysis(fiveAnalysis.value));

/** 오행분석에서 필요한 데이터 분리 */
function parseFiveAnalysis(text) {
  const iljuMatch = text.match(/^(.+?)로/);
  const attributeMatch = text.match(/가장 강한[^.]+입니다\./);

  const iljuName = iljuMatch ? iljuMatch[1].trim() : '';
  const iljuAttribute = attributeMatch ? attributeMatch[0].trim() : '';

  const charMap = {
    목: 'thu',
    화: 'tue',
    토: 'sat',
    금: 'fri',
    수: 'wed',
  };

  const parts = [];
  let lastIndex = 0;
  const regex = /\*\*(.+?)\*\*/g;
  let match;

  while ((match = regex.exec(iljuAttribute)) !== null) {
    if (match.index > lastIndex) {
      parts.push({
        text: iljuAttribute.slice(lastIndex, match.index),
        isKeyword: false,
      });
    }

    const keywordText = match[1].trim();
    const firstChar = keywordText[0];
    const keywordClass = charMap[firstChar] || '';

    parts.push({
      text: `“${keywordText}”`,
      isKeyword: true,
      keywordClass,
    });

    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < iljuAttribute.length) {
    parts.push({
      text: iljuAttribute.slice(lastIndex),
      isKeyword: false,
    });
  }

  return { iljuName, iljuAttributeParts: parts };
}
</script>

<template>
  <VRow class="full-height">
    <VCol cols="12" :md="halfCol" class="no-pad-col mobile-first">
      <VCard class="personality-card full-height right">
        <div class="detail-card__top-title personality">
          <span class="detail-tit__num personality">3</span>
          <span class="detail-tit__txt personality">
            <span>나를 이루는&nbsp;</span>
            <span>다섯 가지 오행 기운의 구성은?</span>
          </span>
        </div>
        <div class="personality-wrapper with-tit">
          <div class="person-content-box chart bar-line-chart">
            <PersonalFiveChart :chartData="chartData" :chartType="'jijangFive'" />
          </div>

          <div class="person-content-box line">
            <div class="person-content__ilju-title">
              {{ parsedFiveAnalysis.iljuName }}
            </div>
            <PersoanlIljuChart :jijangFive="jijangFive" />
            <span class="person-content__ilju-desc">
              <template v-for="(part, idx) in parsedFiveAnalysis.iljuAttributeParts" :key="idx">
                <span v-if="part.isKeyword" class="keyword" :class="part.keywordClass">
                  {{ part.text }}
                </span>
                <template v-else>
                  {{ part.text }}
                </template>
              </template>
            </span>
          </div>
        </div>
      </VCard>
    </VCol>
    <VCol cols="12" :md="halfCol" class="no-pad-col mobile-second">
      <VCard class="personality-card full-height">
        <div class="personality-wrapper">
          <div class="person-desc-box">
            <div class="desc__title">🌿 계절이 말해주는 나의 모습!</div>
            <div class="desc__body gray">
              <div class="personality-keyword">
                <div class="keyword-bubble">
                  {{ seasonCategory }}
                  <SpeechBubbleTail :color="'#db9823'" class="personality-keyword__tail" />
                </div>
              </div>
              <span class="desc__body-txt" v-html="seasonAttributeHtml" />
            </div>
          </div>
          <div class="person-content-box">
            <div class="content__title">🤝 계절의 기운으로 본 나의 관계 스타일</div>
            <div v-for="(html, idx) in seasonDetail" :key="idx">
              <span class="content__body-txt" v-html="html" />
            </div>
          </div>
        </div>
      </VCard>
    </VCol>
  </VRow>
</template>
